import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { breakpoint, sizes, colors } from 'variables';
import { AnimatedUpcomingLogo, AnimatedCardLogo, AnimatedPlayLogo } from 'components/live/cardIcons';
import { importBambuserLiveShopping, initBambuserLiveShopping, openNotifyModal } from 'components/live/liveFunctions';
import DefaultButton, { ButtonStyle } from 'modules/react-button/defaultButton';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import { ColorNeutralBlack, Spacing16, Spacing32 } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import { useTranslation } from 'react-i18next';
import { OnDemandDescription, UpcomingDescription } from 'autogen/translation-keys/trans-website-liveevent-cmsarticle';
import { RemindMe, SeeLive, SeeLiveNow } from 'autogen/translation-keys/trans-website-liveevent';
import type { LiveEventStatus } from 'autogen/swagger/all';
import type { BambuserViewModel } from 'autogen/swagger/all';
import useRequestContext from 'hooks/user/useRequestContext';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import VideoCookieOverlay from 'components/VideoCookieOverlay/VideoCookieOverlay';
import { rgba } from 'helpers/color';

// #region Styles
const Container = styled.div`
    margin-top: -${Spacing32};

    ${breakpoint.only(sizes.sm)} {
        display: flex;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        align-items: center;
        margin: 0 auto;
    }

    ${breakpoint.up(sizes.xl)} {
        display: flex;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        align-items: center;
        margin: 0 auto;
    }
`;

const LogoWrapper = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;

    ${AnimatedUpcomingLogo},
    ${AnimatedCardLogo},
    ${AnimatedPlayLogo} {
        position: relative;
        cursor: pointer;
    }
`;

const LogoContainer = styled.div<{ isLive: boolean }>`
    display: flex;
`;

const ContentContainer = styled.div<{ isLive: boolean }>`
    position: relative;
    display: block;
    background-color: ${colors.negative};
    margin: 0 8px;
    padding: 8px;

    ${breakpoint.only(sizes.sm)} {
        ${(props) =>
            props.isLive
                ? css`
                      display: none;
                  `
                : css`
                      background-color: ${rgba(colors.negative, 0.8)};
                      margin: ${Spacing16} ${Spacing16} ${Spacing16} auto;
                      max-width: 35%;
                  `};
    }

    ${breakpoint.up(sizes.xl)} {
        ${(props) =>
            props.isLive
                ? css`
                      display: none;
                  `
                : css`
                      background-color: ${rgba(colors.negative, 0.8)};
                      margin: ${Spacing16} ${Spacing16} ${Spacing16} auto;
                      max-width: 35%;
                  `};
    }
`;

const Content = styled.div`
    border: 1px solid ${colors.borderPrimary};
    padding: 30px 40px;
    text-align: center;
    color: ${colors.clubmatasFont};

    ${breakpoint.only(sizes.sm)} {
        border: 1px solid ${colors.negative};
        padding: 20px;
    }

    ${breakpoint.up(sizes.xl)} {
        border: 1px solid ${colors.negative};
        padding: ${Spacing16};
    }

    ${breakpoint.up(sizes.xxl)} {
        padding: 20px;
    }
`;

const Description = styled.p`
    color: ${colors.clubmatasFont};
    font-weight: bold;
    margin-bottom: 0em;

    ${({ theme }) =>
        theme.onDemandLayout &&
        css`
            display: none;

            ${breakpoint.only(sizes.sm)} {
                display: block;
            }

            ${breakpoint.up(sizes.xl)} {
                display: block;
            }
        `}
`;

const EventButton = styled(DefaultButton)`
    margin-top: 20px;
    width: 100%;
    white-space: nowrap;
    text-transform: uppercase;

    ${({ theme }) =>
        theme.onDemandLayout &&
        css`
            display: none;

            ${breakpoint.up(sizes.sm)} {
                display: flex;
            }
        `}
`;

const Wrapper = styled.div<{ $cookiesAccepted?: boolean; $hasImage?: boolean }>`
    position: relative;

    ${({ $cookiesAccepted, $hasImage }) =>
        !$cookiesAccepted &&
        !$hasImage &&
        css`
            height: 300px;
            padding: ${Spacing32};
            background-color: ${ColorNeutralBlack};
        `};
`;

const ImageContainer = styled.div`
    position: relative;
    max-width: 100%;
`;

// #endregion Styles

const ArticleLiveEvent = (props: BambuserViewModel): JSX.Element => {
    const { t } = useTranslation();
    const {
        liveEvent: { allowNotifications, bambuserShowId, id, status, image, liveEventStructuredData },
        deepLink,
        linkAction,
        dateTimeTranslations,
        ...restProps
    } = props;
    const { liveHeadline, onDemandHeadline, upcomingHeadline } = dateTimeTranslations;
    const trackingClass = `tracking_liveEvent_articleClick_item-${id}`;
    const { isClubMatasApp } = useRequestContext();
    const isFunctionalCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.functional);

    let headline;
    let description;

    switch (status) {
        case 'Upcoming':
            headline = upcomingHeadline;
            description = allowNotifications ? t(UpcomingDescription) : null;
            break;
        case 'Live':
            headline = liveHeadline;
            break;
        case 'OnDemand':
            headline = onDemandHeadline;
            description = t(OnDemandDescription);
            break;
        default:
            break;
    }

    useEffect((): void => {
        importBambuserLiveShopping();
        initBambuserLiveShopping();
    }, []);

    const openBambuser = () => {
        window.initBambuserLiveShopping({
            showId: bambuserShowId,
            type: 'overlay',
            deeplink: deepLink
        });
    };

    const getPlayIcon = (pStatus: LiveEventStatus): JSX.Element => {
        switch (pStatus) {
            case 'Upcoming':
                return <AnimatedUpcomingLogo onClick={openBambuser} />;
            case 'Live':
                return <AnimatedPlayLogo onClick={openBambuser} />;
            case 'OnDemand':
            default:
                return <AnimatedCardLogo onClick={openBambuser} />;
        }
    };

    const getAction = (pStatus: LiveEventStatus): JSX.Element | null => {
        switch (pStatus) {
            case 'Upcoming':
                return allowNotifications ? (
                    <EventButton
                        buttonStyle={ButtonStyle.live}
                        className={`tracking_liveEvent_articleNotify_item-${id}`}
                        onClick={() => openNotifyModal(linkAction.url)}
                    >
                        {t(RemindMe)}
                    </EventButton>
                ) : null;
            case 'Live':
                return (
                    <>
                        <EventButton buttonStyle={ButtonStyle.live} className={trackingClass} onClick={openBambuser}>
                            {t(SeeLiveNow)}
                        </EventButton>
                    </>
                );
            case 'OnDemand':
                return (
                    <>
                        <EventButton buttonStyle={ButtonStyle.live} className={trackingClass} onClick={openBambuser}>
                            {t(SeeLive)}
                        </EventButton>
                    </>
                );
            default:
                return null;
        }
    };

    const textBox = (
        <Container>
            <ContentContainer isLive={status === 'Live'}>
                <Content>
                    <Typography.Headline size="M">{headline}</Typography.Headline>
                    {description && <Description>{description}</Description>}
                    {(isFunctionalCookieAccepted || isClubMatasApp) && getAction(status)}
                </Content>
            </ContentContainer>
        </Container>
    );

    if (!isFunctionalCookieAccepted && !isClubMatasApp) {
        return !image ? (
            <Wrapper $cookiesAccepted={false}>
                <VideoCookieOverlay />
            </Wrapper>
        ) : (
            <Wrapper $cookiesAccepted={false} $hasImage={true} {...restProps}>
                <ImageContainer>
                    <PictureDeprecated {...image} />
                    <VideoCookieOverlay />
                </ImageContainer>
            </Wrapper>
        );
    }

    return !image ? (
        textBox
    ) : (
        <Wrapper $cookiesAccepted={true} {...restProps}>
            <script
                type="application/ld+json"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                dangerouslySetInnerHTML={{
                    __html: liveEventStructuredData
                }}
            />
            <ImageContainer>
                <PictureDeprecated {...image} />
                <LogoWrapper>
                    <LogoContainer isLive={status === 'Live'}>{getPlayIcon(status)}</LogoContainer>
                </LogoWrapper>
            </ImageContainer>
            {textBox}
        </Wrapper>
    );
};

export default ArticleLiveEvent;
